import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Rating from "@mui/material/Rating";
import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import ShoppingBag from "@mui/icons-material/ShoppingBag";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import NavigateNext from "@mui/icons-material/NavigateNext";
import LocalShipping from "@mui/icons-material/LocalShipping";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import Cached from "@mui/icons-material/Cached";
import Support from "@mui/icons-material/Support";
import CheckCircle from "@mui/icons-material/CheckCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import Favorite from "@mui/icons-material/Favorite";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RateReviewIcon from "@mui/icons-material/RateReview";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  postRecord,
  handleApiResponse,
  handleApiError,
} from "services/services";
import { AuthContext } from "context/Authentication";
import { redirectToLoginWithReturn } from "utils/authUtils";
import Header from "../../components/Header/Header.jsx";
import Footer from "../../components/Footer/Footer.jsx";
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";
import ProductCarousel from "./ProductCarousel.jsx";
import AdditionalProducts from "../Home/Sections/AdditionalProduct.jsx";
import ProductAddonsSelector from "CommonComponents/ProductAddonsSelector/ProductAddonsSelector.jsx";
import ProductChoiceSelector from "CommonComponents/ProductChoiceSelector/ProductChoiceSelector.jsx";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop";

// API Constants - Updated to use single consolidated endpoint
const API_GetProductSingleDisplay = "products/product/single-display";
const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";
const API_GetCartByUserId = "cart/getbyuserid";
const API_AddToWishlist = "wishlist/create";
const API_DeleteWishlist = "wishlist/delete";
const API_GetWishlistByUserId = "wishlist/getbyuserid";
const API_CheckServiceablePincodes = "shipping/check-serviceable-pincodes";
const API_GetReviewsByCombinationId = "order_reviews/getbycombinationid";
const API_GetAddonsByProductId = "product-addons/getbyproductid-enhanced";

export default function ProductView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { productName, combinationText, combinationId } = useParams();
  const context = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [combinations, setCombinations] = useState([]);
  const [defaultCombination, setDefaultCombination] = useState(null);
  const [selectedCombination, setSelectedCombination] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [media, setMedia] = useState([]);
  const [selectedCombinationMedia, setSelectedCombinationMedia] = useState([]); // New state for selected combination media
  const [inventory, setInventory] = useState(null);
  const [units, setUnits] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [headerRefresh, setHeaderRefresh] = useState(true);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "Hi",
  });
  const [attributes, setAttributes] = useState({});
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Add error state
  const [tabValue, setTabValue] = useState(0);
  const [pincode, setPincode] = useState("");
  const [pincodeStatus, setPincodeStatus] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerType, setMediaViewerType] = useState(null); // 'image' or 'video'
  const [mediaViewerSrc, setMediaViewerSrc] = useState("");
  const [showAddonSelector, setShowAddonSelector] = useState(false);
  const [showChoiceSelector, setShowChoiceSelector] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState({});

  useEffect(() => {
    let isMounted = true;

    const initData = async () => {
      // Performance monitoring - start timer
      const startTime = performance.now();

      setLoading(true);
      setError(null); // Clear previous errors
      await fetchProductDetails(isMounted);
      if (isMounted && context.state.isAuthenticated) {
        await fetchWishlist(isMounted);
        // console.log("Fetching wishlist", productName);
      }
      if (isMounted) {
        setLoading(false);

        // Performance monitoring - end timer
        const endTime = performance.now();
        const loadTime = endTime - startTime;
        // console.log(`ProductView loaded in ${loadTime.toFixed(2)}ms`);

        // Optional: Send metrics to analytics
        if (loadTime > 5000) {
          // console.warn(`Slow page load detected: ${loadTime.toFixed(2)}ms`);
        }
      }
    };

    initData();

    return () => {
      isMounted = false;
    };
  }, [
    productName,
    combinationText,
    combinationId,
    context.state.isAuthenticated,
  ]);

  useEffect(() => {
    let isMounted = true;

    if (selectedCombination && media && isMounted) {
      const selectedMedia = media.filter(
        (mediaItem) =>
          mediaItem.Combination_Id === selectedCombination.Combination_Id
      );

      // console.log(
      //   `Filtering media for combination ${selectedCombination.Combination_Id}:`,
      //   {
      //     totalMedia: media.length,
      //     selectedMedia: selectedMedia.length,
      //     selectedMediaDetails: selectedMedia.map((m) => ({
      //       id: m.Product_Media_ID,
      //       path: m.Media_File_Path,
      //       combId: m.Combination_Id,
      //     })),
      //   }
      // );

      // Update both the filtered media for carousel and active image
      setSelectedCombinationMedia(selectedMedia);

      if (selectedMedia?.length > 0) {
        setActiveImage(selectedMedia[0].Media_File_Path);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [selectedCombination, media]);

  useEffect(() => {
    let isMounted = true;

    if (defaultCombination && isMounted) {
      setSelectedCombination(defaultCombination);
      setInventory(defaultCombination.inventory);
    }

    return () => {
      isMounted = false;
    };
  }, [defaultCombination]);

  useEffect(() => {
    let isMounted = true;

    if (selectedCombination && isMounted) {
      setInventory(selectedCombination.inventory);
    }

    return () => {
      isMounted = false;
    };
  }, [selectedCombination]);

  useEffect(() => {
    let isMounted = true;

    if (
      combinations.length > 0 &&
      !selectedCombination &&
      defaultCombination &&
      isMounted
    ) {
      setSelectedCombination(defaultCombination);
      setInventory(defaultCombination.inventory);
    }

    return () => {
      isMounted = false;
    };
  }, [combinations, defaultCombination]);

  // Initialize selected attributes based on the default combination
  useEffect(() => {
    let isMounted = true;

    if (defaultCombination && isMounted) {
      const initialAttributes = {};

      // Use combination details if available (dynamic attributes)
      if (
        defaultCombination.details &&
        Array.isArray(defaultCombination.details)
      ) {
        defaultCombination.details.forEach((detail) => {
          if (detail.Attribute_Name && detail.Attribute_Value) {
            initialAttributes[detail.Attribute_Name] = detail.Attribute_Value;
          }
        });
      } else {
        // Fallback to combination text parsing if no details available
        const combinationText = defaultCombination.Combination || "";
        const parts = combinationText.split(", ");

        // Map combination parts to attributes
        if (parts.length >= 1) {
          initialAttributes["Color"] = parts[0].trim();
        }
        if (parts.length >= 2) {
          initialAttributes["Size"] = parts[1].trim();
        }
      }

      // console.log(
      //   "Setting initial attributes from default combination:",
      //   initialAttributes
      // );
      setSelectedAttributes(initialAttributes);
      setSelectedCombination(defaultCombination);
      setInventory(defaultCombination.inventory);
    }

    return () => {
      isMounted = false;
    };
  }, [defaultCombination]);

  const fetchProductDetails = async (mountedState) => {
    try {
      console.log("Fetching product details for:", decodeURI(productName));
      console.log(
        "Fetching product details combinationId:",
        combinationId ? decodeURI(combinationId) : "null"
      );
      // Use the new consolidated API endpoint
      const response = await postRecord(API_GetProductSingleDisplay, {
        product_full_name: decodeURI(productName),
        combination_id: combinationId || null,
      });
      console.log("Product details response:", response);
      console.log("Response data structure:", {
        hasProductData: !!response.data?.product_data,
        combinationsCount: response.data?.combinations?.length || 0,
        combinationsStructure: response.data?.combinations?.[0]
          ? Object.keys(response.data.combinations[0])
          : [],
      });
      if (!mountedState) return;

      if (response.status === 200 && response.data) {
        const data = response.data;

        // Validate API response structure
        if (!data.product_data) {
          throw new Error("Missing product data in API response");
        }

        if (!data.combinations || !Array.isArray(data.combinations)) {
          throw new Error(
            "Missing or invalid combinations data in API response"
          );
        }

        // Set product data
        setProduct(data.product_data);
        console.log("Product-----------", data.product_data);

        // Process combinations with new API structure
        const combinationsWithDetails =
          data.combinations?.map((combination, index) => {
            console.log(`Processing combination ${index}:`, {
              hasCombinationData: !!combination.combination_data,
              hasDetails: !!combination.combination_details,
              hasInventory: !!combination.combination_inventory,
              hasMedia: !!combination.combination_media,
            });

            return {
              // Map the combination_data properties to root level for backward compatibility
              ...combination.combination_data,
              details: combination.combination_details || [],
              inventory: combination.combination_inventory || {},
              media: combination.combination_media || [],
              reviews: combination.order_reviews || {
                reviews: [],
                average_rating: 0,
                total_reviews: 0,
              },
            };
          }) || [];

        console.log("Processed combinations:", combinationsWithDetails.length);
        setCombinations(combinationsWithDetails);

        // Set media - collect all media from all combinations
        const allMedia = [];
        data.combinations?.forEach((combination) => {
          if (
            combination.combination_media &&
            Array.isArray(combination.combination_media)
          ) {
            allMedia.push(...combination.combination_media);
          }
        });
        setMedia(allMedia);

        // Find default combination or use provided combination ID
        let targetCombination;
        if (combinationId && combinationsWithDetails.length > 0) {
          targetCombination = combinationsWithDetails.find(
            (combo) =>
              combo.Combination_Id?.toString() === combinationId.toString()
          );
        }

        if (!targetCombination && combinationsWithDetails.length > 0) {
          targetCombination = combinationsWithDetails.find(
            (combo) => combo.Is_Default === "1"
          );
        }

        if (!targetCombination && combinationsWithDetails.length > 0) {
          targetCombination = combinationsWithDetails[0];
        }

        if (targetCombination && mountedState) {
          console.log("Selected target combination:", {
            id: targetCombination.Combination_Id,
            combination: targetCombination.Combination,
            details: targetCombination.details,
            hasInventory: !!targetCombination.inventory,
          });

          setDefaultCombination(targetCombination);
          setSelectedCombination(targetCombination);
          setInventory(targetCombination.inventory);

          // Set initial selected attributes from combination details (dynamic attributes)
          const initialAttributes = {};
          if (
            targetCombination.details &&
            Array.isArray(targetCombination.details)
          ) {
            targetCombination.details.forEach((detail) => {
              if (detail.Attribute_Name && detail.Attribute_Value) {
                initialAttributes[detail.Attribute_Name] =
                  detail.Attribute_Value;
              }
            });
          } else {
            // Fallback to combination text parsing if no details available
            const combinationText = targetCombination.Combination || "";
            const parts = combinationText.split(", ");

            console.log(
              "Parsing combination text:",
              combinationText,
              "into parts:",
              parts
            );

            // Map combination parts to attributes assuming standard format: "Color, Size"
            if (parts.length >= 1) {
              initialAttributes["Color"] = parts[0].trim();
            }
            if (parts.length >= 2) {
              initialAttributes["Size"] = parts[1].trim();
            }
          }

          console.log("Initial attributes set:", initialAttributes);
          setSelectedAttributes(initialAttributes);

          // Set active image and selected combination media from target combination
          if (targetCombination.media && targetCombination.media.length > 0) {
            setSelectedCombinationMedia(targetCombination.media);
            setActiveImage(targetCombination.media[0].Media_File_Path);
          } else {
            // If no media in the combination object, filter from allMedia
            const targetMedia = allMedia.filter(
              (mediaItem) =>
                mediaItem.Combination_Id === targetCombination.Combination_Id
            );
            setSelectedCombinationMedia(targetMedia);
            if (targetMedia.length > 0) {
              setActiveImage(targetMedia[0].Media_File_Path);
            }
          }
        }

        // Extract unique attributes and their values from all combinations
        const attributesMap = {};

        combinationsWithDetails.forEach((combination) => {
          // Extract attributes from combination_details (dynamic attributes)
          if (combination.details && Array.isArray(combination.details)) {
            combination.details.forEach((detail) => {
              if (detail.Attribute_Name && detail.Attribute_Value) {
                if (!attributesMap[detail.Attribute_Name]) {
                  attributesMap[detail.Attribute_Name] = new Set();
                }
                attributesMap[detail.Attribute_Name].add(
                  detail.Attribute_Value
                );
              }
            });
          } else {
            // Fallback to combination text parsing if no details available
            const combinationText = combination.Combination || "";
            const parts = combinationText.split(", ");

            // Extract attributes from combination text
            if (parts.length >= 1) {
              if (!attributesMap["Color"]) attributesMap["Color"] = new Set();
              attributesMap["Color"].add(parts[0].trim());
            }
            if (parts.length >= 2) {
              if (!attributesMap["Size"]) attributesMap["Size"] = new Set();
              attributesMap["Size"].add(parts[1].trim());
            }
          }
        });

        if (mountedState) {
          const attributes = {};
          for (const [key, value] of Object.entries(attributesMap)) {
            attributes[key] = Array.from(value).filter((v) => v); // Filter out empty values
          }
          console.log("Final attributes map:", attributes);
          setAttributes(attributes);
        }
      } else {
        throw new Error("Invalid response format from API");
      }
    } catch (error) {
      if (mountedState) {
        console.log("Error fetching product details:", error);
        setError(error.message || "Failed to load product details");
        setSnackOptions({
          color: "error",
          message: "Failed to load product details. Please try again.",
        });
        setSnackOpen(true);

        // Set loading to false even on error to show the error state
        setLoading(false);
      }
    }
  };

  const handleUnitsChange = (change) => {
    setUnits((prevUnits) => Math.max(1, prevUnits + change));
  };

  // Check if Product Addons feature is enabled in app settings
  const isProductAddonsEnabled = () => {
    if (!context.state.appSettings) return false;
    const addonSetting = context.state.appSettings.find(
      (setting) =>
        setting.Setting_Name === "Product Addons" &&
        setting.Setting_Value === "Enabled" &&
        setting.Is_Active === "1"
    );
    return !!addonSetting;
  };

  // Check if product has available addons
  const checkProductAddons = async () => {
    try {
      const response = await postRecord(API_GetAddonsByProductId, {
        Product_Id: product.Product_Id,
        Combination_Id: selectedCombination.Combination_Id,
      });

      if (
        response.status === 200 &&
        response.data &&
        response.data.length > 0
      ) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.log("No addons found for this product/combination");
      return null;
    }
  };

  // Check if product has choice options
  const hasChoiceOptions = () => {
    if (!product || !product.Choice_Options) return false;

    try {
      let parsed;
      if (typeof product.Choice_Options === "string") {
        parsed = JSON.parse(product.Choice_Options);
      } else {
        parsed = product.Choice_Options;
      }

      // Check for new nested format: [{name: 'Size', options: ['S', 'M', 'L']}]
      if (Array.isArray(parsed)) {
        return (
          parsed.length > 0 &&
          parsed.some(
            (choice) =>
              choice.name &&
              Array.isArray(choice.options) &&
              choice.options.length > 0
          )
        );
      }

      return false;
    } catch (error) {
      console.error("Error parsing choice options:", error);
      return false;
    }
  };

  const addToCart = async () => {
    let isMounted = true;

    if (!context.state.isAuthenticated) {
      const currentPath = location.pathname + location.search;
      redirectToLoginWithReturn(navigate, currentPath);
      return;
    }

    const hasChoices = hasChoiceOptions();
    const hasAddons = isProductAddonsEnabled();
    const choicesSelected = Object.keys(selectedChoices).length > 0;

    // If product has choice options and they haven't been selected yet
    if (hasChoices && !choicesSelected) {
      setShowChoiceSelector(true);
      return;
    }

    // If product has addons available (either no choices needed, or choices already selected)
    if (hasAddons) {
      try {
        const availableAddons = await checkProductAddons();
        if (availableAddons && availableAddons.length > 0) {
          setShowAddonSelector(true);
          return;
        }
      } catch (error) {
        console.log(
          "Error checking addons, proceeding with normal cart addition"
        );
      }
    }

    // Otherwise proceed with direct cart addition (no choices, no addons OR choices selected but no addons)
    const choiceOptions =
      Object.keys(selectedChoices).length > 0 ? selectedChoices : [];
    const forceCreateNew = Object.keys(selectedChoices).length > 0;
    await addToCartDirectly(isMounted, choiceOptions, forceCreateNew);
  };

  const addToCartDirectly = async (
    isMounted = true,
    choiceOptions = [],
    forceCreateNew = false
  ) => {
    setProcessing(true);

    try {
      // Validate required data before proceeding
      if (!product?.Product_Id) {
        console.error("Product_Id is missing:", product);
        if (isMounted) {
          handleApiError(
            { message: "Product information is incomplete" },
            setSnackOptions,
            setProcessing,
            setSnackOpen
          );
        }
        return;
      }

      if (!selectedCombination?.Combination_Id) {
        console.error("Combination_Id is missing:", selectedCombination);
        if (isMounted) {
          handleApiError(
            { message: "Please select a product variant" },
            setSnackOptions,
            setProcessing,
            setSnackOpen
          );
        }
        return;
      }

      if (!inventory?.Total_Selling_Price) {
        console.error("Price information is missing:", inventory);
        if (isMounted) {
          handleApiError(
            { message: "Product price information is missing" },
            setSnackOptions,
            setProcessing,
            setSnackOpen
          );
        }
        return;
      }

      const cartResponse = await postRecord(API_GetCartByUserId, {
        User_Id: context.state.user.User_Id,
      });

      if (!isMounted) return;

      let existingCartItem = null;
      const shouldCheckExisting =
        !forceCreateNew &&
        (!choiceOptions || Object.keys(choiceOptions).length === 0);

      if (
        shouldCheckExisting &&
        cartResponse.status === 200 &&
        cartResponse.data
      ) {
        existingCartItem = cartResponse.data.find(
          (item) => item.Combination_Id === selectedCombination.Combination_Id
        );
      }

      if (existingCartItem) {
        const updatedCartItem = {
          ...existingCartItem,
          Units: existingCartItem.Units + units,
          Total_Price:
            (existingCartItem.Units + units) * existingCartItem.Price_Per_Unit,
          Modified_By: context.state.user.User_Id,
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
        };

        try {
          const response = await postRecord(API_UpdateCart, updatedCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              resetAndPreview
            );
          }
        } catch (error) {
          if (isMounted) {
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      } else {
        const newCartItem = {
          User_Id: context.state.user.User_Id,
          Product_Id: product.Product_Id,
          Combination_Id: selectedCombination.Combination_Id,
          Units: units,
          Price_Per_Unit: inventory.Total_Selling_Price,
          Total_Price: units * inventory.Total_Selling_Price,
          Is_Savefor_Later: "0",
          Added_By: context.state.user.User_Id,
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
        };

        console.log("ProductView - Creating new cart item:", newCartItem);
        console.log("ProductView - Choice options:", choiceOptions);

        try {
          const response = await postRecord(API_AddToCart, newCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              resetAndPreview
            );
          }
        } catch (error) {
          if (isMounted) {
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      }
    } catch (error) {
      if (isMounted) {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      }
    } finally {
      if (isMounted) {
        setProcessing(false);
      }
    }
  };

  // Handle successful choice selection
  const handleChoicesSelected = (choices) => {
    setSelectedChoices(choices);
    setShowChoiceSelector(false);

    // After choices are selected, check if addons are available
    if (isProductAddonsEnabled()) {
      try {
        checkProductAddons().then((availableAddons) => {
          if (availableAddons && availableAddons.length > 0) {
            setShowAddonSelector(true);
          } else {
            // If no addons available, proceed directly to cart with choice options
            addToCartDirectly(true, choices, true);
          }
        });
      } catch (error) {
        console.log(
          "Error checking addons, proceeding with normal cart addition"
        );
        addToCartDirectly(true, choices, true);
      }
    } else {
      // If no addons available, proceed directly to cart with choice options
      addToCartDirectly(true, choices, true);
    }
  };

  // Handle direct cart addition from ProductChoiceSelector
  const handleDirectCartAdd = async (
    choiceOptions = [],
    forceCreateNew = false
  ) => {
    await addToCartDirectly(true, choiceOptions, forceCreateNew);
  };

  // Reset choices when combination changes
  useEffect(() => {
    setSelectedChoices({});
  }, [selectedCombination]);

  // Handle successful addon cart completion
  const handleAddonCartComplete = (selectedAddons) => {
    setShowAddonSelector(false);
    setProcessing(false);

    const addonCount = selectedAddons.length;
    const message =
      addonCount > 0
        ? `Product and ${addonCount} add-on${
            addonCount > 1 ? "s" : ""
          } added to cart!`
        : "Product added to cart!";

    setSnackOptions({
      color: "success",
      message: message,
    });
    setSnackOpen(true);
    resetAndPreview();
  };

  const toggleWishlist = async () => {
    let isMounted = true;

    if (!context.state.isAuthenticated) {
      const currentPath = location.pathname + location.search;
      redirectToLoginWithReturn(navigate, currentPath);
      return;
    }

    setProcessing(true);

    const isInWishlist = wishlist.some(
      (item) => item.Product_Id === product.Product_Id
    );

    if (isInWishlist) {
      const wishlistItem = wishlist?.find(
        (item) => item.Product_Id === product.Product_Id
      );
      try {
        const response = await postRecord(API_DeleteWishlist, {
          Wishlist_Id: wishlistItem.Wishlist_Id,
          Deleted_By: context.state.user.User_Id,
        });
        if (isMounted) {
          handleApiResponse(
            response,
            setSnackOptions,
            setProcessing,
            setSnackOpen,
            resetAndPreview
          );
          fetchWishlist(isMounted);
        }
      } catch (error) {
        if (isMounted) {
          handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
        }
      }
    } else {
      const newWishlistItem = {
        Wishlist_Name: "My Wishlist",
        User_Id: context.state.user.User_Id,
        Product_Id: product.Product_Id,
        Combination_Id: selectedCombination.Combination_Id,
        Added_By: context.state.user.User_Id,
      };

      try {
        const response = await postRecord(API_AddToWishlist, newWishlistItem);
        if (isMounted) {
          handleApiResponse(
            response,
            setSnackOptions,
            setProcessing,
            setSnackOpen,
            resetAndPreview
          );
          fetchWishlist(isMounted);
        }
      } catch (error) {
        if (isMounted) {
          handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
        }
      }
    }
  };

  const fetchWishlist = async (mountedState) => {
    try {
      const response = await postRecord(API_GetWishlistByUserId, {
        User_Id: context.state.user.User_Id,
      });
      if (mountedState && response.status === 200 && response.data) {
        setWishlist(response.data);
      }
    } catch (error) {
      if (mountedState) {
        console.log(error);
      }
    }
  };

  const resetAndPreview = () => {
    setHeaderRefresh(true);
    setProcessing(false);
  };

  const handleAttributeChange = (attributeName, value) => {
    console.log(`Changing ${attributeName} to ${value}`);
    let isMounted = true;

    // Validate inputs
    if (
      !attributeName ||
      !value ||
      !combinations ||
      combinations.length === 0
    ) {
      console.warn("Invalid parameters for attribute change:", {
        attributeName,
        value,
        combinations: combinations.length,
      });
      return;
    }

    // Step 1: Find all combinations matching the new attribute name and value
    const matchingCombinations = combinations.filter((combination) => {
      return (
        combination.details &&
        Array.isArray(combination.details) &&
        combination.details.some(
          (detail) =>
            detail.Attribute_Name === attributeName &&
            detail.Attribute_Value === value
        )
      );
    });

    if (matchingCombinations.length === 0 && isMounted) {
      setSnackOptions({
        color: "warning",
        message: "This option is not available in any combination",
      });
      setSnackOpen(true);
      return;
    }

    // Step 2: Find combinations with the selected attribute value that have inventory
    console.log("Matching combinations:", matchingCombinations);
    const availableCombinations = matchingCombinations.filter(
      (combo) =>
        combo.inventory &&
        combo.inventory.Current_Stock > 0 &&
        combo.Is_Soldout !== "1"
    );

    // If no combinations with inventory are available for this attribute value
    if (availableCombinations.length === 0 && isMounted) {
      setSnackOptions({
        color: "warning",
        message: "This option is currently out of stock",
      });
      setSnackOpen(true);
      return;
    }

    // Step 3: First try to keep current attribute values (except the one being changed)
    let bestMatch = availableCombinations.find((combination) => {
      return Object.keys(selectedAttributes)
        .filter((attrName) => attrName !== attributeName)
        .every((attrName) => {
          const expectedValue = selectedAttributes[attrName];
          // Check if this combination contains the expected value for other attributes
          return (
            combination.details &&
            Array.isArray(combination.details) &&
            combination.details.some(
              (detail) =>
                detail.Attribute_Name === attrName &&
                detail.Attribute_Value === expectedValue
            )
          );
        });
    });

    // If no match with current attributes, take the first available combination
    if (!bestMatch) {
      bestMatch = availableCombinations[0];
      console.log(
        `No combination found with ${attributeName}=${value} that keeps current attributes. Auto-selecting a valid combination.`
      );
    }

    // Create new attributes based on the best match combination details
    const updatedAttributes = {};
    if (bestMatch.details && Array.isArray(bestMatch.details)) {
      bestMatch.details.forEach((detail) => {
        if (detail.Attribute_Name && detail.Attribute_Value) {
          updatedAttributes[detail.Attribute_Name] = detail.Attribute_Value;
        }
      });
    }

    // Log what we're changing to for debugging
    console.log("Selected combination:", bestMatch.Combination_Id);
    console.log("Updated attributes:", updatedAttributes);
    console.log("Inventory:", bestMatch.inventory);

    // Update all state at once if component is still mounted
    if (isMounted) {
      setSelectedCombination(bestMatch);
      setInventory(bestMatch.inventory);
      setSelectedAttributes(updatedAttributes);

      // Update URL to reflect the new combination
      try {
        const combinationText = Object.values(updatedAttributes)
          .filter((val) => val) // Filter out empty values
          .join("-")
          .toLowerCase()
          .replace(/[,\s]+/g, "-"); // Replace commas and spaces with hyphens
        const newUrl = `/ProductView/${productName}/${combinationText}/${bestMatch.Combination_Id}`;

        // Update URL without causing a re-render
        window.history.replaceState(null, "", newUrl);
      } catch (urlError) {
        console.warn("Failed to update URL:", urlError);
      }
    }

    // Show notification if other attributes were changed
    const otherAttributesChanged = Object.keys(updatedAttributes).some(
      (attr) =>
        attr !== attributeName &&
        updatedAttributes[attr] !== selectedAttributes[attr]
    );

    if (otherAttributesChanged && isMounted) {
      setSnackOptions({
        color: "info",
        message: "We've updated other options to match your selection",
      });
      setSnackOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const checkPincode = async () => {
    let isMounted = true;

    try {
      const response = await postRecord(API_CheckServiceablePincodes, {
        pincodes: [pincode],
      });

      if (!isMounted) return;

      console.log("delivery check---", response);
      if (
        response.status === 200 &&
        response.data?.delivery_codes &&
        response.data.delivery_codes.length > 0 &&
        response.data.delivery_codes[0].postal_code?.pre_paid === "Y" &&
        response.data.delivery_codes[0].postal_code?.cod === "Y"
      ) {
        setPincodeStatus("success");
      } else {
        setPincodeStatus("not_found");
      }
    } catch (error) {
      if (isMounted) {
        setPincodeStatus("not_found");
      }
    }
  };

  // Add a function to check if an attribute value has inventory
  const isAttributeValueAvailable = (attributeName, attributeValue) => {
    // Find combinations that match this attribute name and value
    const matchingCombos = combinations.filter((combination) => {
      return (
        combination.details &&
        Array.isArray(combination.details) &&
        combination.details.some(
          (detail) =>
            detail.Attribute_Name === attributeName &&
            detail.Attribute_Value === attributeValue
        )
      );
    });

    // Check if any of these combinations have inventory
    return matchingCombos.some(
      (combo) =>
        combo.inventory &&
        combo.inventory.Current_Stock > 0 &&
        combo.Is_Soldout !== "1"
    );
  };

  const getColorStyle = (colorName) => {
    // Convert to lowercase for consistency
    const normalizedColor = colorName.toLowerCase().trim();

    // Try to use the name as a CSS color
    try {
      // Create temporary element to check if color is valid
      const tempEl = document.createElement("div");
      tempEl.style.color = normalizedColor;

      // If the browser can interpret the color
      if (tempEl.style.color) {
        // For standard CSS colors, create softer versions with opacity
        return {
          border: `rgba(${getColorComponents(normalizedColor, 0.4)})`,
          text: `rgba(${getColorComponents(normalizedColor, 0.6)})`,
          selected: `rgba(${getColorComponents(normalizedColor, 0.9)})`,
        };
      }
    } catch (e) {
      // Handle any parsing errors
    }

    // Handle a few special cases that aren't standard CSS colors
    const specialCases = {
      wine: { r: 114, g: 47, b: 55 },
      peach: { r: 255, g: 229, b: 180 },
      lavender: { r: 230, g: 230, b: 250 },
    };

    if (specialCases[normalizedColor]) {
      const { r, g, b } = specialCases[normalizedColor];
      return {
        border: `rgba(${r}, ${g}, ${b}, 0.4)`,
        text: `rgba(${r}, ${g}, ${b}, 0.6)`,
        selected: `rgba(${r}, ${g}, ${b}, 0.9)`,
      };
    }

    // Default to a neutral gray if we can't determine the color
    return {
      border: "rgba(128, 128, 128, 0.3)",
      text: "rgba(128, 128, 128, 0.6)",
      selected: "rgba(128, 128, 128, 0.9)",
    };
  };

  // Helper function to extract RGB components with opacity
  const getColorComponents = (colorName, opacity = 0.5) => {
    const tempEl = document.createElement("div");
    tempEl.style.color = colorName;
    document.body.appendChild(tempEl);

    const computedColor = window.getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    // Extract RGB values from computed style
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const [, r, g, b] = rgbMatch;
      return `${r}, ${g}, ${b}, ${opacity}`;
    }

    // Default if extraction fails
    return `128, 128, 128, ${opacity}`;
  };

  const getContrastTextColor = (backgroundColor) => {
    try {
      // Extract RGB components from rgba string
      const rgbaMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbaMatch) {
        const [, r, g, b] = rgbaMatch;

        // Calculate brightness using the formula
        // (299*R + 587*G + 114*B) / 1000
        const brightness =
          (299 * parseInt(r) + 587 * parseInt(g) + 114 * parseInt(b)) / 1000;

        // Return white for dark backgrounds, black for light backgrounds
        return brightness > 125 ? "rgba(0, 0, 0, 0.87)" : "#fff";
      }

      // Default to black text if we can't determine
      return "rgba(0, 0, 0, 0.87)";
    } catch (e) {
      return "rgba(0, 0, 0, 0.87)";
    }
  };

  // Add effect to fetch reviews when selectedCombination changes
  useEffect(() => {
    let isMounted = true;

    if (selectedCombination && selectedCombination.Combination_Id) {
      fetchReviewsByCombination(selectedCombination.Combination_Id, isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [selectedCombination]);

  // Add function to fetch reviews by combination ID
  const fetchReviewsByCombination = async (combinationId, mountedState) => {
    try {
      const response = await postRecord(API_GetReviewsByCombinationId, {
        Combination_Id: combinationId,
      });

      if (!mountedState) return;

      if (response.status === 200 && response.data) {
        setReviews(response.data);

        // Calculate average rating
        if (response.data.length > 0) {
          const totalRating = response.data.reduce(
            (sum, review) => sum + Number(review.Rating),
            0
          );
          setAverageRating(totalRating / response.data.length);
          setReviewCount(response.data.length);
        } else {
          setAverageRating(0);
          setReviewCount(0);
        }
      }
    } catch (error) {
      if (mountedState) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
        setAverageRating(0);
        setReviewCount(0);
      }
    }
  };

  // TabPanel helper with consistent padding
  function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`product-tabpanel-${index}`}
        aria-labelledby={`product-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ py: 3, px: 3 }}>{children}</Box>}
      </div>
    );
  }

  // Handler to open media viewer
  const handleOpenMediaViewer = (type, src) => {
    setMediaViewerType(type);
    setMediaViewerSrc(src);
    setMediaViewerOpen(true);
  };
  const handleCloseMediaViewer = () => {
    setMediaViewerOpen(false);
    setMediaViewerType(null);
    setMediaViewerSrc("");
  };

  return (
    <React.Fragment>
      <Box sx={{ minHeight: "100vh" }}>
        <Header
          headerRefresh={headerRefresh}
          setHeaderRefresh={setHeaderRefresh}
        />
        <Container
          maxWidth="xl"
          sx={{
            py: { xs: 3, md: 10 },
            mt: { xs: 5, md: 2 },
            minHeight: "80vh",
          }}
        >
          {/* Breadcrumb Section */}
          <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
            <Stack spacing={1}>
              <Breadcrumbs
                separator={
                  <ChevronRightIcon
                    sx={{ color: "text.secondary", opacity: 0.6 }}
                  />
                }
                aria-label="breadcrumb"
                sx={{
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 1, md: 1.5 },
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(30,30,40,0.7)"
                      : "rgba(245,245,250,0.7)",
                  boxShadow: 1,
                  fontWeight: 500,
                  fontSize: { xs: 12, md: 14 },
                  alignItems: "center",
                  "& .MuiBreadcrumbs-separator": {
                    mx: { xs: 0.25, md: 0.5 },
                  },
                }}
              >
                <Button
                  color="inherit"
                  variant="text"
                  size="small"
                  startIcon={
                    <HomeOutlinedIcon sx={{ color: "primary.main" }} />
                  }
                  onClick={() => navigate("/")}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.5, md: 1 },
                    py: { xs: 0.25, md: 0.5 },
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  Home
                </Button>
                <Button
                  color="inherit"
                  variant="text"
                  size="small"
                  startIcon={<CategoryIcon sx={{ color: "primary.main" }} />}
                  onClick={() => {
                    // Special handling for School Menu category
                    if (
                      product?.Category_Name?.toLowerCase() === "school menu"
                    ) {
                      navigate("/FoodMenuCalendar");
                    } else {
                      navigate(
                        `/ProductList/${product?.Category_Name?.toLowerCase().replace(
                          / /g,
                          "-"
                        )}`
                      );
                    }
                  }}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.5, md: 1 },
                    py: { xs: 0.25, md: 0.5 },
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {product && product.Category_Name}
                </Button>
                <Button
                  color="inherit"
                  variant="text"
                  size="small"
                  startIcon={
                    <SubdirectoryArrowRightIcon
                      sx={{ color: "primary.main" }}
                    />
                  }
                  onClick={() => {
                    // Special handling for School Menu category
                    if (
                      product?.Category_Name?.toLowerCase() === "school menu"
                    ) {
                      navigate("/FoodMenuCalendar");
                    } else {
                      navigate(
                        `/ProductList/${product?.Category_Name?.toLowerCase().replace(
                          / /g,
                          "-"
                        )}/${product?.Sub_Category_Name?.toLowerCase().replace(
                          / /g,
                          "-"
                        )}`
                      );
                    }
                  }}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.5, md: 1 },
                    py: { xs: 0.25, md: 0.5 },
                    color: "text.primary",
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {product && product.Sub_Category_Name}
                </Button>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, md: 1 },
                    ml: { xs: 0.25, md: 0.5 },
                  }}
                >
                  <LocalOfferIcon
                    sx={{
                      color: "primary.main",
                      fontSize: { xs: 16, md: 20 },
                      mr: { xs: 0.25, md: 0.5 },
                    }}
                  />
                  <Typography
                    color="textPrimary"
                    fontWeight={700}
                    sx={{ fontSize: { xs: 11, md: 14 } }}
                  >
                    {product && product.Product_Full_Name}
                    {selectedCombination && selectedCombination.details && (
                      <>
                        {" "}
                        <Typography
                          component="span"
                          color="primary"
                          fontWeight={600}
                          sx={{ fontSize: { xs: 10, md: 13 }, ml: 0.5 }}
                        >
                          {Object.values(selectedCombination.details)
                            .map((d) => d.Attribute_Value)
                            .join(" / ")}
                        </Typography>
                      </>
                    )}
                  </Typography>
                </Box>
              </Breadcrumbs>
            </Stack>
          </Box>

          {error ? (
            // Error state
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                textAlign: "center",
                py: 4,
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 80,
                  color: "error.main",
                  mb: 2,
                  opacity: 0.7,
                }}
              />
              <Typography variant="h5" color="error" gutterBottom>
                Product Not Found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 400 }}
              >
                We couldn't load the product details. This might be due to the
                product being unavailable or a temporary issue.
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  startIcon={<ArrowBackIcon />}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  startIcon={<RefreshIcon />}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          ) : product && !loading ? (
            <Grid container spacing={{ xs: 2, md: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 0,
                    height: { xs: "auto", md: 800 },
                    overflow: "hidden",
                  }}
                >
                  <ProductCarousel
                    media={selectedCombinationMedia}
                    activeImage={activeImage}
                    setActiveImage={setActiveImage}
                  />
                </Paper>
                <Box
                  sx={{
                    mt: 2,
                    py: { xs: 1, md: 4 },
                    px: { xs: 0.5, md: 2 },
                    borderRadius: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: { xs: 0.5, md: 0 },
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: 1,
                  }}
                >
                  <Stack alignItems="center" spacing={{ xs: 0.25, md: 1 }}>
                    <LocalShipping
                      color="primary"
                      sx={{ fontSize: { xs: 16, md: 30 } }}
                    />
                    <Typography
                      variant={{ xs: "caption", md: "body2" }}
                      align="center"
                      sx={{ fontSize: { xs: "0.6rem", md: "0.875rem" } }}
                    >
                      Super Fast Shipping
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={{ xs: 0.25, md: 1 }}>
                    <Cached
                      color="primary"
                      sx={{ fontSize: { xs: 16, md: 30 } }}
                    />
                    <Typography
                      variant={{ xs: "caption", md: "body2" }}
                      align="center"
                      sx={{ fontSize: { xs: "0.6rem", md: "0.875rem" } }}
                    >
                      Trusted Experience
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={{ xs: 0.25, md: 1 }}>
                    <VerifiedUser
                      color="primary"
                      sx={{ fontSize: { xs: 16, md: 30 } }}
                    />
                    <Typography
                      variant={{ xs: "caption", md: "body2" }}
                      align="center"
                      sx={{ fontSize: { xs: "0.6rem", md: "0.875rem" } }}
                    >
                      Quality Assured
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={{ xs: 0.25, md: 1 }}>
                    <Support
                      color="primary"
                      sx={{ fontSize: { xs: 16, md: 30 } }}
                    />
                    <Typography
                      variant={{ xs: "caption", md: "body2" }}
                      align="center"
                      sx={{ fontSize: { xs: "0.6rem", md: "0.875rem" } }}
                    >
                      24/7 Support
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={{ xs: 0.25, md: 1 }}>
                    <CheckCircle
                      color="primary"
                      sx={{ fontSize: { xs: 16, md: 30 } }}
                    />
                    <Typography
                      variant={{ xs: "caption", md: "body2" }}
                      align="center"
                      sx={{ fontSize: { xs: "0.6rem", md: "0.875rem" } }}
                    >
                      Secure Payment
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 1, md: 4 },
                    height: { xs: "auto", md: 800 },
                    overflow: "auto",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      mb: { xs: 0.5, md: 1 },
                      fontSize: { xs: "1.5rem", md: "2.125rem" },
                    }}
                  >
                    {product.Product_Full_Name}
                  </Typography>

                  {/* Add rating display below product name */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: { xs: 0.5, md: 2 },
                    }}
                  >
                    <Rating
                      value={averageRating}
                      readOnly
                      precision={0.5}
                      size="small"
                      sx={{ color: "primary.main" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 1,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                      }}
                    >
                      {reviewCount > 0 ? (
                        <>
                          {averageRating.toFixed(1)} ({reviewCount}{" "}
                          {reviewCount === 1 ? "review" : "reviews"})
                        </>
                      ) : (
                        "No reviews yet"
                      )}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 0.5, md: 2 },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    }}
                  >
                    {product.Short_Description}
                  </Typography>

                  {/* Show price only if inventory is available */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: { xs: 0.5, md: 2 },
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: { xs: 1, md: 2 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Show striked-out MRP price only if it's different from selling price */}
                      {inventory &&
                        inventory.Total_MRP_Price !==
                          inventory.Total_Selling_Price && (
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: "line-through",
                              color: "gray",
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                            }}
                          >
                            <small
                              style={{
                                fontWeight: "500",
                                fontSize: "8px",
                                marginRight: "2px",
                              }}
                            >
                              {context.state?.companyInfo?.Currency}
                            </small>
                            {inventory.Total_MRP_Price?.toFixed(2)}
                          </Typography>
                        )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "1rem", md: "1.25rem" },
                        }}
                      >
                        <small
                          style={{
                            fontWeight: "500",
                            fontSize: "8px",
                            marginRight: "2px",
                          }}
                        >
                          {context.state?.companyInfo?.Currency}
                        </small>
                        {inventory
                          ? (inventory.Total_Selling_Price * units).toFixed(2)
                          : 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        onClick={() => handleUnitsChange(-1)}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                      >
                        <Remove />
                      </IconButton>
                      <TextField
                        value={units}
                        size="small"
                        sx={{
                          width: { xs: "40px", md: "50px" },
                          textAlign: "center",
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            fontSize: { xs: "0.875rem", md: "1rem" },
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <IconButton
                        onClick={() => handleUnitsChange(1)}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Revert back to the original attribute selection UI with toggle buttons */}
                  <Box
                    sx={{
                      mt: { xs: 2, md: 4 },
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    {Object.keys(attributes).map((attributeName) => (
                      <Box key={attributeName} sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {attributeName}
                        </Typography>
                        <ToggleButtonGroup
                          value={selectedAttributes[attributeName] || ""}
                          exclusive
                          onChange={(e, value) => {
                            if (value)
                              handleAttributeChange(attributeName, value);
                          }}
                          sx={{ flexWrap: "wrap" }}
                        >
                          {attributes[attributeName].map((value) => {
                            // Check if this is a color attribute
                            const isColorAttribute =
                              attributeName.toLowerCase() === "color" ||
                              attributeName.toLowerCase() === "colour";

                            // Check if this attribute value is available (has inventory)
                            const isAvailable = isAttributeValueAvailable(
                              attributeName,
                              value
                            );

                            // Get color style for color attributes
                            let colorStyle = {};
                            if (isColorAttribute) {
                              const dynamicColor = getColorStyle(value);

                              // Create a style with appropriate contrast for text based on the background color
                              colorStyle = {
                                // For selected state - background is the color
                                "&.Mui-selected": {
                                  backgroundColor: dynamicColor.border,
                                  color: getContrastTextColor(
                                    dynamicColor.border
                                  ),
                                  borderColor: dynamicColor.selected,
                                  fontWeight: "medium",
                                  "&:hover": {
                                    backgroundColor: dynamicColor.selected,
                                  },
                                },
                                // For normal state - just a colored border
                                backgroundColor: "transparent", // Will adapt to theme
                                borderColor: dynamicColor.border,
                                color: "text.primary", // Will adapt to theme
                              };
                            }

                            return (
                              <ToggleButton
                                key={value}
                                value={value}
                                disabled={!isAvailable}
                                sx={{
                                  textTransform: "none",
                                  // For non-color attributes
                                  ...(isColorAttribute
                                    ? colorStyle // Use color-specific styles
                                    : {
                                        // For normal state of non-color attributes
                                        backgroundColor:
                                          "rgba(128, 128, 128, 0.1)", // Light gray that works in both modes
                                        borderColor: "divider", // Theme's divider color
                                        color: "text.primary", // Theme's text color
                                        // For selected state of non-color attributes
                                        "&.Mui-selected": {
                                          backgroundColor: "primary.main",
                                          color: "primary.contrastText",
                                          fontWeight: "medium",
                                          "&:hover": {
                                            backgroundColor: "primary.dark",
                                          },
                                        },
                                      }),
                                  opacity: isAvailable ? 1 : 0.5,
                                }}
                              >
                                {value}
                              </ToggleButton>
                            );
                          })}
                        </ToggleButtonGroup>
                      </Box>
                    ))}
                  </Box>

                  <Typography variant="body2" sx={{ mt: { xs: 2, md: 4 } }}>
                    {product.Long_Description}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: { xs: 2, md: 4 },
                      mb: { xs: 1, md: 2 },
                      fontWeight: "medium",
                    }}
                  >
                    Specifications:
                  </Typography>
                  <List dense sx={{ ml: 1 }}>
                    {product?.Product_Specifications_File_Path &&
                      Object.entries(
                        JSON.parse(product?.Product_Specifications_File_Path)
                      ).map(([, value], index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              color="primary"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={value}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { color: "text.primary" },
                            }}
                          />
                        </ListItem>
                      ))}
                  </List>

                  {/* Action buttons for cart and wishlist OR School Menu notice */}
                  {product?.Category_Name?.toLowerCase() === "school menu" ? (
                    // School Menu Notice - Replaces action buttons
                    <Box
                      sx={{
                        mt: { xs: 2, md: 3 },
                        p: 3,
                        borderRadius: 3,
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(56, 142, 60, 0.15))"
                            : "linear-gradient(135deg, rgba(232, 245, 232, 0.8), rgba(240, 248, 240, 0.9))",
                        border: (theme) =>
                          theme.palette.mode === "dark"
                            ? "1px solid rgba(76, 175, 80, 0.3)"
                            : "1px solid rgba(76, 175, 80, 0.5)",
                        backdropFilter: "blur(10px)",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                            : "0 8px 32px rgba(76, 175, 80, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <InfoIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 28,
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "text.primary",
                            fontSize: { xs: "1rem", md: "1.125rem" },
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          📅 School Menu Item
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.875rem", md: "0.9rem" },
                            lineHeight: 1.5,
                          }}
                        >
                          This item is available through our daily school menu
                          system. Please check the Food Menu Calendar to view
                          daily availability, scheduling, and place orders for
                          specific dates.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    // Regular action buttons for non-school menu items
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: { xs: 2, md: 3 },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={addToCart}
                        startIcon={
                          processing ? null : (
                            <AddShoppingCartIcon className="button-icon" />
                          )
                        }
                        sx={{
                          py: 1.5,
                          px: 3,
                          fontSize: "16px",
                          fontWeight: 600,
                          borderRadius: 3,
                          borderWidth: "2px",
                          position: "relative",
                          overflow: "hidden",
                          background:
                            "linear-gradient(45deg, transparent, transparent)",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          whiteSpace: "nowrap",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background: (theme) =>
                              `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
                            transition:
                              "left 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                            zIndex: -1,
                          },
                          "&:hover": {
                            color: "white",
                            borderColor: "transparent",
                            transform: "translateY(-2px)",
                            boxShadow: (theme) =>
                              `0 12px 30px ${theme.palette.primary.main}40, 0 6px 20px ${theme.palette.secondary.main}30`,
                            "&::before": {
                              left: 0,
                            },
                            "& .button-icon": {
                              transform: "translateX(8px) scale(1.1)",
                              opacity: 0,
                            },
                            "& .button-text": {
                              transform: "translateX(-8px)",
                            },
                            "& .hover-icon": {
                              transform: "translateX(0px) scale(1.1)",
                              opacity: 1,
                            },
                          },
                          "&:active": {
                            transform: "translateY(0px)",
                          },
                          "& .button-icon": {
                            fontSize: "1.25rem",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: "translateX(0px) scale(1)",
                            opacity: 1,
                          },
                          "& .button-text": {
                            transition:
                              "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: "translateX(0px)",
                            display: "inline",
                          },
                          "& .hover-icon": {
                            position: "absolute",
                            left: "16px",
                            fontSize: "1.25rem",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: "translateX(-8px) scale(1)",
                            opacity: 0,
                          },
                        }}
                      >
                        {processing ? (
                          <CircularProgress
                            size={24}
                            color="inherit"
                            sx={{
                              animation: "pulse 1.5s ease-in-out infinite",
                              "@keyframes pulse": {
                                "0%": { opacity: 1 },
                                "50%": { opacity: 0.5 },
                                "100%": { opacity: 1 },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <ArrowForwardIcon className="hover-icon" />
                            <span className="button-text">
                              {hasChoiceOptions() &&
                              Object.keys(selectedChoices || {}).length === 0
                                ? "Select Options"
                                : "Add to Cart"}
                            </span>
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={toggleWishlist}
                        sx={{
                          py: 1.5,
                          px: 3,
                          fontSize: "16px",
                          fontWeight: 600,
                          borderRadius: 3,
                          border: "none",
                          minWidth: 56,
                          position: "relative",
                          overflow: "hidden",
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(30,30,40,0.6)"
                              : "rgba(255,255,255,0.8)",
                          backdropFilter: "blur(16px)",
                          boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                              ? "0 4px 20px rgba(0,0,0,0.3)"
                              : "0 4px 20px rgba(0,0,0,0.1)",
                          transform: "translateY(0)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "0",
                            height: "0",
                            borderRadius: "50%",
                            background: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.1)"
                                : `${theme.palette.primary.main}10`,
                            transform: "translate(-50%, -50%)",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          },
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0 8px 30px rgba(0,0,0,0.4)"
                                : `0 8px 30px ${theme.palette.primary.main}20`,
                            "&::before": {
                              width: "100%",
                              height: "100%",
                            },
                          },
                          "&:active": {
                            transform: "translateY(0)",
                          },
                        }}
                      >
                        {processing ? (
                          <CircularProgress
                            size={24}
                            color="inherit"
                            sx={{
                              animation: "pulse 1.5s ease-in-out infinite",
                              "@keyframes pulse": {
                                "0%": { opacity: 1 },
                                "50%": { opacity: 0.5 },
                                "100%": { opacity: 1 },
                              },
                            }}
                          />
                        ) : wishlist.some(
                            (item) => item.Product_Id === product.Product_Id
                          ) ? (
                          <Favorite
                            sx={{
                              color: "#ff4757",
                              animation: "heartBeat 1.5s ease-in-out infinite",
                              "@keyframes heartBeat": {
                                "0%": { transform: "scale(1)" },
                                "14%": { transform: "scale(1.1)" },
                                "28%": { transform: "scale(1)" },
                                "42%": { transform: "scale(1.1)" },
                                "70%": { transform: "scale(1)" },
                              },
                            }}
                          />
                        ) : (
                          <FavoriteBorder
                            sx={{
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "scale(1.1)",
                              },
                            }}
                          />
                        )}
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            // Enhanced loading skeleton
            <Grid container spacing={{ xs: 2, md: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{ p: 0, height: { xs: 300, md: 800 } }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{ borderRadius: 1 }}
                  />
                </Paper>
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rectangular"
                      width={60}
                      height={60}
                      animation="wave"
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{ p: { xs: 1, md: 4 }, height: { xs: "auto", md: 800 } }}
                >
                  <Skeleton
                    variant="text"
                    height={60}
                    width="80%"
                    animation="wave"
                  />
                  <Skeleton
                    variant="text"
                    height={30}
                    width="60%"
                    animation="wave"
                    sx={{ mt: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    height={40}
                    width="40%"
                    animation="wave"
                    sx={{ mt: 2 }}
                  />
                  <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                    <Skeleton
                      variant="rectangular"
                      width={100}
                      height={40}
                      animation="wave"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={100}
                      height={40}
                      animation="wave"
                    />
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    {[1, 2, 3].map((line) => (
                      <Skeleton
                        key={line}
                        variant="text"
                        height={20}
                        width={`${Math.random() * 30 + 70}%`}
                        animation="wave"
                        sx={{ mt: 0.5 }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height={50}
                      animation="wave"
                    />
                    <Skeleton
                      variant="circular"
                      width={50}
                      height={50}
                      animation="wave"
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Product Tabs Section with consistent padding and always visible tabs */}
          {product && (
            <Box sx={{ mt: 2, mb: 4 }}>
              <Paper elevation={3} sx={{ borderRadius: 2 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  indicatorColor="primary"
                  textColor="primary"
                  aria-label="product tabs"
                >
                  <Tab
                    label="About Product"
                    icon={<InfoIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Additional Information"
                    icon={<DescriptionIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label={`Reviews (${reviewCount})`}
                    icon={<RateReviewIcon />}
                    iconPosition="start"
                  />
                </Tabs>
                <TabPanel value={tabValue} index={1}>
                  <Typography variant="h6" sx={{ mt: 1, width: "100%" }}>
                    Additional Information:
                  </Typography>
                  <div
                    style={{ width: "100%" }}
                    dangerouslySetInnerHTML={{
                      __html: product.Product_AdditionalInfo_File_Path,
                    }}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={0}>
                  <Typography variant="h6" sx={{ mt: 1, width: "100%" }}>
                    About Product:
                  </Typography>
                  <div
                    style={{ width: "100%" }}
                    dangerouslySetInnerHTML={{
                      __html: product.About_Product_File_Path,
                    }}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <Typography variant="h6" gutterBottom>
                    Customer Reviews
                  </Typography>
                  {reviews.length > 0 ? (
                    <Box>
                      <Divider sx={{ mb: 3 }} />
                      {reviews.map((review, idx) => {
                        // Parse Review_Media for images and videos
                        let images = [];
                        let videos = [];
                        if (review.Review_Media) {
                          let media = review.Review_Media;
                          if (typeof media === "string") {
                            try {
                              media = JSON.parse(media);
                            } catch (e) {
                              media = {};
                            }
                          }
                          images = Array.isArray(media.images)
                            ? media.images
                            : [];
                          videos = Array.isArray(media.videos)
                            ? media.videos
                            : [];
                        }
                        // Combine images and videos in one row, preserving order: images first, then videos
                        const mediaRow = [
                          ...images.map((img, i) => ({
                            type: "image",
                            src: process.env.BASEURL + img,
                            key: `img-${i}`,
                          })),
                          ...videos.map((vid, i) => ({
                            type: "video",
                            src: process.env.BASEURL + vid,
                            key: `vid-${i}`,
                          })),
                        ];
                        return (
                          <Box
                            key={idx}
                            sx={{
                              mb: 3,
                              pb: 3,
                              borderBottom: idx < reviews.length - 1 ? 1 : 0,
                              borderColor: "divider",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                                <VerifiedUser />
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Verified User
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {new Date(
                                    review.Added_On
                                  ).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                            <Rating
                              value={review.Rating}
                              readOnly
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="body1">
                              {review.Review_Text}
                            </Typography>
                            {/* Show images and videos in one row */}
                            {mediaRow.length > 0 && (
                              <Box
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  gap: 2,
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                {mediaRow.map((media, i) =>
                                  media.type === "image" ? (
                                    <img
                                      key={media.key}
                                      src={media.src}
                                      alt={`Review Image ${i + 1}`}
                                      style={{
                                        height: 100,
                                        width: "auto",
                                        aspectRatio: "16/9",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        objectFit: "cover",
                                        background: "#eee",
                                        display: "block",
                                      }}
                                      onClick={() =>
                                        handleOpenMediaViewer(
                                          "image",
                                          media.src
                                        )
                                      }
                                    />
                                  ) : (
                                    <Box
                                      key={media.key}
                                      sx={{
                                        position: "relative",
                                        display: "inline-block",
                                      }}
                                    >
                                      <video
                                        src={media.src}
                                        style={{
                                          height: 100,
                                          width: "auto",
                                          aspectRatio: "16/9",
                                          borderRadius: 4,
                                          cursor: "pointer",
                                          display: "block",
                                          objectFit: "cover",
                                          background: "#eee",
                                        }}
                                        onClick={() =>
                                          handleOpenMediaViewer(
                                            "video",
                                            media.src
                                          )
                                        }
                                        muted
                                        preload="metadata"
                                      />
                                      <PlayCircleOutlineIcon
                                        sx={{
                                          position: "absolute",
                                          top: "50%",
                                          left: "50%",
                                          transform: "translate(-50%, -50%)",
                                          color: "#fff",
                                          fontSize: 40,
                                          pointerEvents: "none",
                                          opacity: 0.85,
                                          textShadow:
                                            "0 0 8px #000, 0 0 16px #000",
                                        }}
                                      />
                                    </Box>
                                  )
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                      {/* Media Viewer Backdrop */}
                      <Backdrop
                        open={mediaViewerOpen}
                        onClick={handleCloseMediaViewer}
                        sx={{
                          zIndex: (theme) => theme.zIndex.drawer + 1,
                          color: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100vw",
                            height: "100vh",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(0,0,0,0.9)",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            onClick={handleCloseMediaViewer}
                            sx={{
                              position: "absolute",
                              top: 24,
                              right: 24,
                              color: "#fff",
                              zIndex: 2,
                              background: "rgba(0,0,0,0.5)",
                            }}
                          >
                            <CloseIcon fontSize="large" />
                          </IconButton>
                          {mediaViewerType === "image" ? (
                            <img
                              src={mediaViewerSrc}
                              alt="Full Review"
                              style={{
                                maxWidth: "90vw",
                                maxHeight: "90vh",
                                borderRadius: 8,
                                boxShadow: "0 0 24px #000",
                              }}
                            />
                          ) : mediaViewerType === "video" ? (
                            <video
                              src={mediaViewerSrc}
                              controls
                              autoPlay
                              style={{
                                maxWidth: "90vw",
                                maxHeight: "90vh",
                                borderRadius: 8,
                                boxShadow: "0 0 24px #000",
                              }}
                            />
                          ) : null}
                        </Box>
                      </Backdrop>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <RateReviewIcon
                        sx={{
                          fontSize: 60,
                          color: "text.secondary",
                          opacity: 0.5,
                          mb: 2,
                        }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        No reviews yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Be the first to review this product
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
              </Paper>
            </Box>
          )}

          {/* Related products section - now with improved layout and spacing */}
          <Box sx={{ mt: 5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 3, textAlign: "center" }}
            >
              You May Also Like
            </Typography>
            {/* <AdditionalProducts
              categoryId={product?.Category_Id}
              productId={product?.Product_Id}
              currentCombinationId={selectedCombination?.Combination_Id}
            /> */}
          </Box>
        </Container>
        <Footer />

        {/* Product Choice Options Selector Dialog */}
        <ProductChoiceSelector
          open={showChoiceSelector}
          onClose={() => setShowChoiceSelector(false)}
          product={product}
          selectedCombination={selectedCombination}
          units={units}
          onChoicesSelected={handleChoicesSelected}
          setParentSelectedChoices={setSelectedChoices}
          onAddToCart={handleDirectCartAdd}
          resetAndPreview={resetAndPreview}
          isProductAddonsEnabled={isProductAddonsEnabled}
          checkProductAddons={checkProductAddons}
          setShowAddonSelector={setShowAddonSelector}
        />

        {/* Product Addons Selector Dialog */}
        <ProductAddonsSelector
          open={showAddonSelector}
          onClose={() => setShowAddonSelector(false)}
          product={product}
          selectedCombination={selectedCombination}
          units={units}
          onAddToCartComplete={handleAddonCartComplete}
          mainProductPrice={inventory?.Total_Selling_Price || 0}
          selectedChoices={selectedChoices}
        />

        <SnackBar
          open={snackOpen}
          setOpen={setSnackOpen}
          options={snackOptions}
        />
      </Box>
    </React.Fragment>
  );
}
